import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_DAY_MONTH_NAME } from 'constants/dateFormats'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'

const SizedFilterSelectBox = styled(OfferFilterDropdownBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  /**
   * Check in date coming directly from state
   */
  stateCheckIn?: string
  /**
   * Check out date coming directly from state
   */
  stateCheckOut?: string
  isOpen: boolean
  inSidebar?: boolean
  ref?: React.Ref<HTMLButtonElement>
  /**
   * Explicitly tell this component is a controlled component
   * and we will be passing in our own check in/check out
   */
  controlled?: boolean
  /**
   * The current check in date
   * This value will only be used if controlled is true
   */
  checkIn?: string
  /**
   * The current check out date
   * This value will only be used if controlled is true
   */
  checkOut?: string
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
}

function OfferDatePickerSelectBox(props: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const {
    stateCheckIn,
    stateCheckOut,
    checkIn: controlledCheckIn,
    checkOut: controlledCheckOut,
    isOpen,
    inSidebar,
    ref,
    controlled,
    onClick = noop,
  } = props

  const checkIn = controlled ? controlledCheckIn : stateCheckIn
  const checkOut = controlled ? controlledCheckOut : stateCheckOut

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkOut ? moment(checkOut) : undefined, [checkOut])
  const selectedDuration = useMemo(() => checkOutMoment?.diff(checkInMoment, 'days'), [checkInMoment, checkOutMoment])
  const areDatesValid = checkIn && checkOut
  const dispatch = useOfferPageDispatch()

  const onBoxClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
      onClick(e)
    }
  }, [dispatch, isOpen, onClick, trackEvent])

  const nightsCountLabel = intl.formatMessage({
    id: 'MlngVClhVmMyAyNZAGpj',
    defaultMessage: '({count, plural, one {# night} other {# nights}})',
    description: 'Parenthesised night count in date picker select box',
  }, { count: selectedDuration ?? 1 })

  const dateRangeLabel = intl.formatMessage(
    {
      id: 'aI3UKP7lvy7IoV0z4tOL',
      defaultMessage: '{checkIn} - {checkOut}',
      description: 'Date range showing check-in and check-out dates on the offer detail page',
    },
    {
      checkIn: dateFormatter(checkIn, INTL_DAY_MONTH_NAME),
      checkOut: dateFormatter(checkOut, INTL_DAY_MONTH_NAME),
    },
  )

  const dateValue = areDatesValid ? <><b>{dateRangeLabel}</b> {nightsCountLabel}</> : undefined

  return (
    <SizedFilterSelectBox
      onClick={onBoxClick}
      active={isOpen}
      ref={ref}
      id="date-picker"
      data-testid="OfferDatePickerSelectBox"
      icon={<LineCalendarIcon />}
      inSidebar={inSidebar}
      label={intl.formatMessage({
        id: 'W_7jAdEEow5p087vogV1',
        defaultMessage: 'When',
        description: 'Label for the date picker filter input in the search and filtering interface',
      })}
      placeholder={intl.formatMessage({
        id: '6bGNDul72dbFQF-sTHBF',
        defaultMessage: 'Select your dates',
        description: 'Placeholder text for date picker in the date selection or calendar picker',
      })}
      value={dateValue}
    />
  )
}

type MappedProps = Pick<Props, 'stateCheckIn' | 'stateCheckOut' | 'isOpen'>

export default withOfferPageState((state): MappedProps => ({
  stateCheckIn: state.checkInDate,
  stateCheckOut: state.checkOutDate,
  isOpen: state.activeFilter === 'checkin',
}), OfferDatePickerSelectBox)
